const FRUIT_SYMBOL_VARIANT_ID = 'fruit';
const DEFAULT_SYMBOL_VARIANT_ID = 'screws-box';

const clampLevel = (level) => Math.max(0, Number.isFinite(Number(level)) ? Number(level) : 0);

const createScenarioEntry = ({
    id,
    familyId,
    label,
    status,
    variantId,
    playfield,
    silhouetteRisk = 'medium',
    summary = '',
    boardPrompt = '',
    sheetPrompt = '',
    symbolCountGoal = 24,
    notes = '',
}) => ({
    id,
    familyId,
    label,
    status,
    variantId,
    playfield,
    silhouetteRisk,
    summary,
    boardPrompt,
    sheetPrompt,
    symbolCountGoal,
    notes,
});

const IMAGE_SCENARIOS = [
    createScenarioEntry({
        id: 'fruit-crate',
        familyId: FRUIT_SYMBOL_VARIANT_ID,
        label: 'Fruit Crate',
        status: 'live',
        variantId: 'fruit-crate',
        playfield: 'fruit crate board',
        silhouetteRisk: 'medium',
        summary: 'Recovered fruit atlas symbols on the fruit crate board.',
        boardPrompt: 'Recovered fruit crate board atlas theme.',
        sheetPrompt: 'Recovered transparent 5x4 fruit atlas with 20 fruit symbols.',
        symbolCountGoal: 20,
        notes: 'Recovered from the old fruit atlas and moved into the scenarios theme folder.',
    }),
    createScenarioEntry({
        id: 'egg-nest',
        familyId: FRUIT_SYMBOL_VARIANT_ID,
        label: 'Egg Nest',
        status: 'live',
        variantId: 'egg-nest',
        playfield: 'woven nest or egg carton',
        silhouetteRisk: 'high',
        summary: 'Stylized eggs and nest-side objects. Needs exaggerated silhouette differences to stay readable.',
        boardPrompt: '',
        sheetPrompt: '',
        symbolCountGoal: 20,
        notes: 'Useful concept, but it needs heavy art-direction control because eggs alone are too similar.',
    }),
    createScenarioEntry({
        id: 'graduation-orla',
        familyId: FRUIT_SYMBOL_VARIANT_ID,
        label: 'Graduation Orla',
        status: 'live',
        variantId: 'graduation-orla',
        playfield: 'graduation celebration board',
        silhouetteRisk: 'medium',
        summary: 'Graduation portrait faces on a square celebration board with a clear center.',
        boardPrompt: 'Top-down empty graduation celebration board with caps, diplomas, ribbons, transparent background, bold border, clear center, no text.',
        sheetPrompt: 'Transparent 5x4 sheet with 20 distinct graduation portrait faces, bold dark outlines, simple glossy mobile game style.',
        symbolCountGoal: 20,
        notes: 'Uses the standard square image playfield; the rejected fixed 5x5 Orla grid is not used.',
    }),
    createScenarioEntry({
        id: 'screws-box',
        familyId: FRUIT_SYMBOL_VARIANT_ID,
        label: 'Screws Box',
        status: 'live',
        variantId: 'screws-box',
        playfield: 'hardware organizer tray',
        silhouetteRisk: 'low',
        summary: 'Screws, bolts, washers, nuts, anchors, and bits inside a parts box.',
        boardPrompt: 'Top-down empty hardware organizer tray for a casual mobile game, transparent background, clean compartments, no screws.',
        sheetPrompt: 'Transparent set with 16 distinct hardware fastener icons for a casual mobile game. Screws and bolts with strong variation and consistent style.',
        symbolCountGoal: 16,
        notes: 'One of the strongest future packs for clarity and silhouette variety.',
    }),
    createScenarioEntry({
        id: 'buttons-tin',
        familyId: FRUIT_SYMBOL_VARIANT_ID,
        label: 'Buttons Tin',
        status: 'planned',
        variantId: FRUIT_SYMBOL_VARIANT_ID,
        playfield: 'sewing tin',
        silhouetteRisk: 'medium',
        summary: 'Buttons, toggles, snaps, bobbins, and sewing notions in a craft tin.',
        boardPrompt: 'Top-down empty sewing tin or notions tray for a casual mobile game, transparent background, tidy shallow border, no buttons.',
        sheetPrompt: 'Transparent 6x4 sheet with 24 distinct sewing notion icons for a casual mobile game. Buttons, toggles, snaps, bobbins, thread spools, and thimbles with bold silhouette differences.',
        symbolCountGoal: 24,
        notes: 'Works well if the pack includes more than just round buttons.',
    }),
    createScenarioEntry({
        id: 'shell-tray',
        familyId: FRUIT_SYMBOL_VARIANT_ID,
        label: 'Shell Tray',
        status: 'live',
        variantId: 'shell-tray',
        playfield: 'beachcomber tray',
        silhouetteRisk: 'medium',
        summary: 'Seashells, coral bits, starfish shapes, and smooth stones in a tray.',
        boardPrompt: 'Top-down empty beachcomber tray for a casual mobile game, transparent background, light natural border, no shells.',
        sheetPrompt: 'Transparent set with 10 distinct shell icons for a casual mobile game. Shell silhouettes with clean flat shading.',
        symbolCountGoal: 10,
        notes: 'Live single-family shell pack using the uploaded transparent shell symbols.',
    }),
    createScenarioEntry({
        id: 'bolt-bin',
        familyId: FRUIT_SYMBOL_VARIANT_ID,
        label: 'Bolt Bin',
        status: 'planned',
        variantId: FRUIT_SYMBOL_VARIANT_ID,
        playfield: 'industrial parts bin',
        silhouetteRisk: 'low',
        summary: 'Hex bolts, sockets, nuts, spacers, and clamps in a dense workshop bin.',
        boardPrompt: 'Top-down empty industrial parts bin for a casual mobile game, transparent background, sturdy inner border, no hardware.',
        sheetPrompt: 'Transparent 6x4 sheet with 24 distinct industrial hardware icons for a casual mobile game. Hex bolts, sockets, nuts, spacers, washers, clamps, and clips with strong readable silhouettes.',
        symbolCountGoal: 24,
        notes: 'Pairs well with the screw scenario and keeps the same gameplay readability.',
    }),
    createScenarioEntry({
        id: 'bead-tray',
        familyId: FRUIT_SYMBOL_VARIANT_ID,
        label: 'Bead Tray',
        status: 'planned',
        variantId: FRUIT_SYMBOL_VARIANT_ID,
        playfield: 'craft bead tray',
        silhouetteRisk: 'medium',
        summary: 'Beads, charms, pendants, and jewel-cut pieces in a craft organizer.',
        boardPrompt: 'Top-down empty craft bead tray for a casual mobile game, transparent background, segmented tray border, no beads.',
        sheetPrompt: 'Transparent 6x4 sheet with 24 distinct bead and charm icons for a casual mobile game. Use varied silhouettes such as stars, droplets, cubes, hearts, rings, and faceted gems.',
        symbolCountGoal: 24,
        notes: 'Needs varied charm silhouettes, not only round beads.',
    }),
    createScenarioEntry({
        id: 'leaf-basket',
        familyId: FRUIT_SYMBOL_VARIANT_ID,
        label: 'Leaf Basket',
        status: 'live',
        variantId: 'leaf-basket',
        playfield: 'woven harvest basket',
        silhouetteRisk: 'medium',
        summary: 'Leaves, acorns, cones, seed pods, and bark pieces in a basket.',
        boardPrompt: 'Top-down empty harvest basket for a casual mobile game, transparent background, soft woven border, no leaves.',
        sheetPrompt: 'Transparent 6x4 sheet with 24 distinct autumn foraging icons for a casual mobile game. Leaves, acorns, cones, seed pods, bark curls, and twig pieces with clear silhouettes.',
        symbolCountGoal: 24,
        notes: 'Live single-family leaf pack. No acorns, cones, flowers, or fruit as searchable symbols.',
    }),
    createScenarioEntry({
        id: 'key-hook-board',
        familyId: FRUIT_SYMBOL_VARIANT_ID,
        label: 'Key Hook Board',
        status: 'live',
        variantId: 'key-hook-board',
        playfield: 'entryway key board',
        silhouetteRisk: 'low',
        summary: 'Keys, tags, fobs, car remotes, and padlocks on a wall board.',
        boardPrompt: 'Top-down empty key board or entryway organizer for a casual mobile game, transparent background, simple frame, no keys.',
        sheetPrompt: 'Transparent set with 20 distinct key icons for a casual mobile game. Keys with clear silhouette differences.',
        symbolCountGoal: 20,
        notes: 'Very good readability pack if the key heads differ strongly.',
    }),
    createScenarioEntry({
        id: 'dog-breed-board',
        familyId: FRUIT_SYMBOL_VARIANT_ID,
        label: 'Dog Breed Board',
        status: 'live',
        variantId: 'dog-breed-board',
        playfield: 'dog breed show board',
        silhouetteRisk: 'low',
        summary: 'Dog breed head icons on a dog-show board with ribbons and paw textures.',
        boardPrompt: 'Top-down empty dog breed show board for a casual mobile game, transparent background, ribbon corners, no dogs.',
        sheetPrompt: 'Transparent 5x4 sheet with 20 distinct dog breed head icons, bold outlines, simple glossy mobile game style.',
        symbolCountGoal: 20,
        notes: 'Strong silhouette variety from breed head shapes and ears.',
    }),
    createScenarioEntry({
        id: 'pillbox-board',
        familyId: FRUIT_SYMBOL_VARIANT_ID,
        label: 'Pillbox Board',
        status: 'live',
        variantId: 'pillbox-board',
        playfield: 'weekly pillbox tray',
        silhouetteRisk: 'medium',
        summary: 'Single-family pill/tablet/capsule symbols on a pillbox board.',
        boardPrompt: 'Top-down empty pillbox tray board, transparent background, square centered playfield, bold border, no text.',
        sheetPrompt: 'Transparent 4x4 sheet with 16 pills only: capsules, tablets, caplets, and scored pills with small visual differences.',
        symbolCountGoal: 16,
        notes: 'Keep the searchable elements restricted to pills only.',
    }),
    createScenarioEntry({
        id: 'flower-garden',
        familyId: FRUIT_SYMBOL_VARIANT_ID,
        label: 'Flower Garden',
        status: 'live',
        variantId: 'flower-garden',
        playfield: 'square garden bed',
        silhouetteRisk: 'medium',
        summary: 'Single-family flower symbols on a garden board.',
        boardPrompt: 'Top-down empty flower garden board, transparent background, square centered playfield, bold border, clear center, no text.',
        sheetPrompt: 'Transparent 4x4 sheet with 16 flowers only, bold outlines, simple glossy mobile game style.',
        symbolCountGoal: 16,
        notes: 'No garden tools, insects, pots, or standalone leaves in the symbol set.',
    }),
    createScenarioEntry({
        id: 'parking-garage',
        familyId: FRUIT_SYMBOL_VARIANT_ID,
        label: 'Parking Garage',
        status: 'live',
        variantId: 'parking-garage',
        playfield: 'parking garage floor',
        silhouetteRisk: 'medium',
        summary: 'Single-family car symbols on a square parking garage board.',
        boardPrompt: 'Top-down empty parking garage board, transparent background, square centered playfield, bold border, clear center, no text.',
        sheetPrompt: 'Transparent 4x4 sheet with 16 cars only, similar vehicle family, varied silhouettes and colors.',
        symbolCountGoal: 16,
        notes: 'No garage props, traffic cones, signs, tools, or wheels as standalone symbols.',
    }),
    createScenarioEntry({
        id: 'little-cars',
        familyId: FRUIT_SYMBOL_VARIANT_ID,
        label: 'Little Cars',
        status: 'hidden',
        variantId: 'little-cars',
        playfield: 'little car road board',
        silhouetteRisk: 'medium',
        summary: 'Small car symbols on the uploaded little cars scenario board.',
        boardPrompt: 'Uploaded little cars scenario board with transparent background.',
        sheetPrompt: 'Transparent set with 10 small car symbols, grouped by dominant body color.',
        symbolCountGoal: 10,
        notes: 'Prepared for runtime and preview, but hidden from user-facing theme selection and random rotation.',
    }),
    createScenarioEntry({
        id: 'planes-sky',
        familyId: FRUIT_SYMBOL_VARIANT_ID,
        label: 'Planes Sky',
        status: 'live',
        variantId: 'planes-sky',
        playfield: 'square sky board',
        silhouetteRisk: 'medium',
        summary: 'Single-family plane symbols on a sky board.',
        boardPrompt: 'Top-down flat sky board, transparent background, square centered playfield, bold border, clear center, no text.',
        sheetPrompt: 'Transparent 4x4 sheet with 16 aircraft only, similar plane family, varied silhouettes and colors.',
        symbolCountGoal: 16,
        notes: 'No clouds, towers, suitcases, balloons, or helicopters in the symbol set.',
    }),
    createScenarioEntry({
        id: 'rock-carrier-box',
        familyId: FRUIT_SYMBOL_VARIANT_ID,
        label: 'Rock Carrier Box',
        status: 'live',
        variantId: 'rock-carrier-box',
        playfield: 'geology specimen carrier box',
        silhouetteRisk: 'low',
        summary: 'Rocks, pebbles, geodes, mineral shards, and polished specimens inside a field carrier box.',
        boardPrompt: 'Top-down empty geology specimen carrier box for a casual mobile game, transparent background, sturdy quiet inner border, no rocks.',
        sheetPrompt: 'Transparent 6x4 sheet with 24 distinct rock and mineral icons for a casual mobile game. Pebbles, geodes, crystal chunks, slate pieces, pumice, basalt, quartz, and layered stones with clear silhouette variety.',
        symbolCountGoal: 24,
        notes: 'Live single-family rock/mineral pack. Keep the board calm and the searchable symbols restricted to stones/minerals.',
    }),
];

const SCENARIO_FAMILIES = {
    [FRUIT_SYMBOL_VARIANT_ID]: IMAGE_SCENARIOS,
};

const normalizeScenarioId = (scenarioId) => `${scenarioId || ''}`.trim().toLowerCase();

const normalizeScenarioFamilyId = () => FRUIT_SYMBOL_VARIANT_ID;

const getUFOVScenarioCatalog = (familyId = FRUIT_SYMBOL_VARIANT_ID) => (
    [...(SCENARIO_FAMILIES[normalizeScenarioFamilyId(familyId)] || IMAGE_SCENARIOS)]
);

const getUFOVScenarioById = (scenarioId) => {
    const normalizedId = normalizeScenarioId(scenarioId);

    return [
        ...IMAGE_SCENARIOS,
    ].find((scenario) => scenario.id === normalizedId) || null;
};

const getUFOVLiveScenarioCatalog = (familyId = FRUIT_SYMBOL_VARIANT_ID) => (
    getUFOVScenarioCatalog(familyId).filter((scenario) => scenario.status === 'live')
);

const getUFOVPlannedScenarioCatalog = (familyId = FRUIT_SYMBOL_VARIANT_ID) => (
    getUFOVScenarioCatalog(familyId).filter((scenario) => scenario.status !== 'live')
);

const normalizeScenarioRng = (rng) => (
    typeof rng === 'function' ? rng : Math.random
);

const pickScenarioEntry = (entries, level, options = {}) => {
    if (!entries.length) {
        return null;
    }

    if (options.pickMode === 'random') {
        const rng = normalizeScenarioRng(options.rng);
        return entries[Math.floor(rng() * entries.length)] || entries[0];
    }

    return entries[clampLevel(level) % entries.length];
};

const getUFOVScenarioRuntimeContext = (familyId, level, options = {}) => {
    const normalizedFamilyId = normalizeScenarioFamilyId(familyId);
    const normalizedScenarioId = normalizeScenarioId(
        typeof options === 'string' ? options : options?.scenarioId
    );
    const runtimeOptions = typeof options === 'string' ? {} : options;
    const familyCatalog = getUFOVScenarioCatalog(normalizedFamilyId);
    const liveCatalog = getUFOVLiveScenarioCatalog(normalizedFamilyId);
    const rotationPool = liveCatalog.length ? liveCatalog : familyCatalog;
    const requestedScenario = familyCatalog.find((scenario) => scenario.id === normalizedScenarioId)
        || pickScenarioEntry(rotationPool, level, runtimeOptions);
    const activeScenario = requestedScenario?.status === 'live'
        ? requestedScenario
        : pickScenarioEntry(liveCatalog, level, runtimeOptions)
        || requestedScenario
        || IMAGE_SCENARIOS[0];

    return {
        familyId: normalizedFamilyId,
        level: clampLevel(level),
        requestedScenario,
        activeScenario,
        activeVariantId: activeScenario?.variantId || DEFAULT_SYMBOL_VARIANT_ID,
    };
};

const getUFOVRuntimeVariantIdForLevel = (familyId, level) => (
    getUFOVScenarioRuntimeContext(familyId, level).activeVariantId
);

module.exports = {
    DEFAULT_SYMBOL_VARIANT_ID,
    FRUIT_SYMBOL_VARIANT_ID,
    getUFOVLiveScenarioCatalog,
    getUFOVPlannedScenarioCatalog,
    getUFOVRuntimeVariantIdForLevel,
    getUFOVScenarioById,
    getUFOVScenarioCatalog,
    getUFOVScenarioRuntimeContext,
    normalizeScenarioFamilyId,
    normalizeScenarioId,
};
module.exports.default = module.exports;
